import { BaseEffect, EasingFunction, Easing, CanvasDimensions, EffectResult, MultiTrackEffect, LayerType } from './BaseEffect';

/**
 * MultiTrackZoomEffect applies zoom transformation to multiple layers simultaneously.
 * Unlike regular ZoomEffect which applies to individual elements, this effect targets
 * entire layer containers, allowing coordinated zoom across multiple tracks.
 * 
 * @param from - Starting zoom (1 = 100%, or 100 if isPercent=true)
 * @param to - Ending zoom (1 = 100%, or 100 if isPercent=true)
 * @param duration - Duration in seconds
 * @param at - Start time in seconds
 * @param targetLayers - Array of layer types to apply the effect to
 * @param excludeLayers - Optional array of layer types to exclude from the effect
 * @param easing - Easing function
 * @param isPercent - If true, from/to are percentages (e.g., 100 for 100%)
 */
export class MultiTrackZoomEffect extends BaseEffect implements MultiTrackEffect {
    from: number; // normalized zoom value (1 = 100%)
    to: number;   // normalized zoom value (1 = 100%)
    targetLayers: LayerType[];
    excludeLayers: LayerType[];
    
    // BaseEffect's `containerEffect` flag is already true via the constructor call.
    // Public accessor for MultiTrackEffect interface compatibility
    get containerBased(): boolean {
        return this.containerEffect;
    }

    constructor(
        from: number, 
        to: number, 
        duration: number, 
        at: number,
        targetLayers: LayerType[] = ['background', 'scene', 'overlay', 'textOverlay'],
        excludeLayers: LayerType[] = ['caption', 'branding'],
        easing: EasingFunction = Easing.easeOutCubic,
        isPercent: boolean = false
    ) {
        super('multitrack-zoom', duration, at, easing, true); // containerEffect = true
        this.from = isPercent ? from / 100 : from;
        this.to = isPercent ? to / 100 : to;
        this.targetLayers = targetLayers;
        this.excludeLayers = excludeLayers || [];
    }

    // Required by Effect interface - not used for multi-track effects but must be implemented
    apply(
        ctx: CanvasRenderingContext2D,
        dimensions: CanvasDimensions,
        progress: number,
        prev: EffectResult
    ): EffectResult {
        // Multi-track effects don't use canvas operations - they use CSS transforms
        return prev;
    }

    // Primary method for multi-track effects - returns CSS transform styles
    getContainerStyle(
        progress: number,
        dimensions?: CanvasDimensions,
        effectResult?: EffectResult
    ): React.CSSProperties {
        const currentZoom = this.from + (this.to - this.from) * progress;
        
        // Apply zoom transform with proper origin centering
        return {
            transform: `scale(${currentZoom})`,
            transformOrigin: '50% 50%', // Center the zoom transformation
            transition: 'none' // Prevent CSS transitions from interfering with Remotion
        };
    }

    // Helper method to check if a layer should be affected by this effect
    shouldApplyToLayer(layerType: LayerType): boolean {
        // If excludeLayers is specified and contains this layer, exclude it
        if (this.excludeLayers.includes(layerType)) {
            return false;
        }
        
        // If targetLayers is specified, only apply to listed layers
        return this.targetLayers.includes(layerType);
    }

    // Static factory methods for common use cases
    static createExcludingCaptions(
        from: number,
        to: number,
        duration: number,
        at: number,
        easing?: EasingFunction
    ): MultiTrackZoomEffect {
        return new MultiTrackZoomEffect(
            from, to, duration, at,
            ['background', 'scene', 'overlay', 'overlayTransition', 'textOverlay'],
            ['caption', 'branding'],
            easing
        );
    }

    static createMainContentOnly(
        from: number,
        to: number,
        duration: number,
        at: number,
        easing?: EasingFunction
    ): MultiTrackZoomEffect {
        return new MultiTrackZoomEffect(
            from, to, duration, at,
            ['background', 'scene', 'overlay'],
            ['overlayTransition', 'textOverlay', 'caption', 'branding'],
            easing
        );
    }

    static createAllExceptBranding(
        from: number,
        to: number,
        duration: number,
        at: number,
        easing?: EasingFunction
    ): MultiTrackZoomEffect {
        return new MultiTrackZoomEffect(
            from, to, duration, at,
            ['background', 'scene', 'overlay', 'overlayTransition', 'textOverlay', 'caption'],
            ['branding'],
            easing
        );
    }
}